import {
  exceedsMaxPrecision,
  FieldValidationLevel,
  toBigWithDefault,
  type FieldValidationRule,
  type NumericField,
} from '@talos/kyoko';
import type Big from 'big.js';

interface IncrementPrecisionValidationContext {
  increment?: string;
  incrementName: string;
}

/**
 * Warns when a numeric field has more decimal places than the provided increment allows.
 */
const fieldExceedsIncrementPrecision: FieldValidationRule<NumericField, IncrementPrecisionValidationContext> = (
  field,
  context
) => {
  if (!field.value || !context?.increment) {
    return null;
  }

  if (!exceedsMaxPrecision(field.value, context.increment)) {
    return null;
  }

  return {
    message: `${field.name} exceeds the decimal precision implied by ${context.incrementName} (${context.increment}). Submission is still allowed.`,
    level: FieldValidationLevel.Warning, // <-- note that we only ever warn on precision issues
  };
};

interface FillAmountValidationContext {
  amountIncrement?: string;
  derivedFillAmount?: Big;
}

/**
 * Warns when Fill Amount exceeds the decimal precision implied by currency MinIncrement.
 */
export const fillAmountExceedsIncrementPrecision: FieldValidationRule<NumericField, FillAmountValidationContext> = (
  field,
  context
) => {
  return fieldExceedsIncrementPrecision(field, {
    increment: context?.amountIncrement,
    incrementName: 'MinIncrement',
  });
};

/**
 * Warns when a manually entered Fill Amount no longer matches the currently derived amount.
 */
export const fillAmountDiffersFromDerived: FieldValidationRule<NumericField, FillAmountValidationContext> = (
  field,
  context
) => {
  if (context?.derivedFillAmount == null || field.bigValue == null || field.bigValue.eq(context.derivedFillAmount)) {
    return null;
  }

  return {
    message: `Differs from derived amount ${context.derivedFillAmount.toFixed()}`,
    level: FieldValidationLevel.Warning,
  };
};

/**
 * Warns when Fill Price exceeds the decimal precision implied by security MinPriceIncrement.
 */
export const fillPriceExceedsIncrementPrecision: FieldValidationRule<NumericField, { priceIncrement?: string }> = (
  field,
  context
) => {
  return fieldExceedsIncrementPrecision(field, {
    increment: context?.priceIncrement,
    incrementName: 'MinPriceIncrement',
  });
};

interface FillQtyValidationContext {
  leavesQty: string;
  quantityIncrement?: string;
}

/**
 * Errors when Fill Quantity is not exactly equal to the care order Leaves Quantity.
 */
export const fillQtyEqualsCareOrderLeavesQty: FieldValidationRule<NumericField, FillQtyValidationContext> = (
  field,
  context
) => {
  if (!field.value || !context) {
    return null;
  }

  if (!toBigWithDefault(field.value, 0).eq(context.leavesQty)) {
    return {
      message: 'Fill Quantity must equal the care order Leaves Quantity',
      level: FieldValidationLevel.Error,
    };
  }

  return null;
};

/**
 * Warns when Fill Quantity exceeds the decimal precision implied by security MinSizeIncrement.
 */
export const fillQtyExceedsIncrementPrecision: FieldValidationRule<NumericField, FillQtyValidationContext> = (
  field,
  context
) => {
  return fieldExceedsIncrementPrecision(field, {
    increment: context?.quantityIncrement,
    incrementName: 'MinSizeIncrement',
  });
};
